/**
 * PerChildContractorSendBundle · iter303ak-fix24b — Accepted · T30 · Action 40
 * ──────────────────────────────────────────────────────────────────────────
 * Per-contractor MOCKED send. Reads items from the previous action
 * (per_child_contractor_report_preview.items) then, on mount, enriches
 * each row with contractor info fetched from
 *   /api/appraisals/{child_id}/contractor-overview → type_assignments[ctype]
 *   /api/contractors/{contractor_id}
 *
 * Each row exposes an editable Email + Mobile field (pre-filled from the
 * contractor record). Send button hits POST /appraisals/{child_id}/
 * contractor-pdf/{ctype}/email with contractor_id + to_email. If the
 * operator edited the email/mobile and no contractor was on file yet, we
 * skip the update; if a contractor IS on file and the operator edited,
 * we offer to persist the change back to the contractor record.
 *
 * Stamps `contractor_report_sent_at.{child_id}.{ctype}` on the parent
 * appraisal after each successful send.
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, Mail, AlertCircle, Send, User, Save, AlertTriangle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function PerChildContractorSendBundle({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentAppraisalId = capturedData?.appraisal_id;
  const stampFieldPrefix = params.stamp_field_prefix || 'contractor_report_sent_at';

  // Items to send = previous action's include=true list.
  const items = useMemo(() => {
    const preview = capturedData?.per_child_contractor_report_preview
      || capturedData?.[params.items_source]
      || {};
    const raw = Array.isArray(preview?.items) ? preview.items : [];
    return raw.filter(i => i.include !== false);
  }, [capturedData, params.items_source]);

  // Row-level state: { "child_id|ctype": { contractor_id, contractor_name, email, mobile, loading, savingBack } }
  const [rowState, setRowState] = useState(() => value?.row_state || {});
  const [sendState, setSendState] = useState(() => value?.send_state || {});
  const [busyKeys, setBusyKeys] = useState(() => new Set());
  const [globalErr, setGlobalErr] = useState('');
  const [enriched, setEnriched] = useState(false);

  // Enrich each row on mount with contractor info.
  const enrichRows = useCallback(async () => {
    if (!items.length || enriched) return;
    const next = { ...rowState };
    // Cache overviews per child_id (to avoid refetching for same child multiple ctypes)
    const overviewCache = {};
    for (const it of items) {
      const key = `${it.child_id}|${it.ctype}`;
      if (next[key] && next[key].contractor_id !== undefined) continue; // already enriched
      let overview = overviewCache[it.child_id];
      if (!overview) {
        try {
          const { data } = await api.get(`/appraisals/${it.child_id}/contractor-overview`);
          overview = data;
          overviewCache[it.child_id] = data;
        } catch {
          overview = { type_assignments: {} };
        }
      }
      const cid = (overview.type_assignments || {})[it.ctype] || '';
      let contractor_name = '';
      let email = '';
      let mobile = '';
      if (cid) {
        try {
          const { data: cdoc } = await api.get(`/contractors/${cid}`);
          contractor_name = cdoc?.contractor_name || cdoc?.company_name || '';
          email = cdoc?.email || '';
          mobile = cdoc?.contact_number || '';
        } catch { /* silent */ }
      }
      next[key] = {
        contractor_id: cid,
        contractor_name,
        email,
        mobile,
        email_orig: email,
        mobile_orig: mobile,
      };
    }
    setRowState(next);
    setEnriched(true);
  }, [items, enriched]);

  useEffect(() => { enrichRows(); }, [enrichRows]);

  const isSent = (child_id, ctype) => !!sendState[`${child_id}|${ctype}`]?.sent_at;
  const errFor = (child_id, ctype) => sendState[`${child_id}|${ctype}`]?.error;

  const updateRow = (key, patch) => {
    setRowState(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  };

  const isEmailValid = (e) => !!(e && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e.trim()));

  const sendOne = async (child_id, ctype) => {
    const key = `${child_id}|${ctype}`;
    const row = rowState[key] || {};
    if (!isEmailValid(row.email)) {
      setSendState(prev => ({ ...prev, [key]: { error: 'Enter a valid email above before sending.' } }));
      return;
    }
    setBusyKeys(prev => { const n = new Set(prev); n.add(key); return n; });
    setGlobalErr('');
    try {
      const payload = {
        to_email: (row.email || '').trim(),
      };
      if (row.contractor_id) payload.contractor_id = row.contractor_id;
      const r = await api.post(`/appraisals/${child_id}/contractor-pdf/${encodeURIComponent(ctype)}/email`, payload);
      const nowIso = new Date().toISOString();
      setSendState(prev => ({
        ...prev,
        [key]: { sent_at: nowIso, ok: true, response: r?.data || {}, to_email: payload.to_email },
      }));
      if (parentAppraisalId) {
        try {
          const existing = capturedData?.appraisal?.[stampFieldPrefix] || {};
          await api.patch(`/appraisals/${parentAppraisalId}`, {
            [stampFieldPrefix]: {
              ...existing,
              [child_id]: {
                ...(existing[child_id] || {}),
                [ctype]: nowIso,
              },
            },
          });
        } catch { /* silent */ }
      }
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e?.message || 'Send failed';
      setSendState(prev => ({ ...prev, [key]: { error: errMsg } }));
      setGlobalErr(errMsg);
    } finally {
      setBusyKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  const saveContractorRecord = async (key) => {
    const row = rowState[key] || {};
    if (!row.contractor_id) return;
    setRowState(prev => ({ ...prev, [key]: { ...prev[key], savingBack: true } }));
    try {
      // Fetch current record so we can send back a full payload (PUT expects the full ContractorInput).
      const { data: cdoc } = await api.get(`/contractors/${row.contractor_id}`);
      const payload = {
        contractor_name: cdoc?.contractor_name || '',
        company_name: cdoc?.company_name || '',
        contact_number: (row.mobile || '').trim(),
        email: (row.email || '').trim(),
        address: cdoc?.address || '',
        gst_number: cdoc?.gst_number || '',
        specialties: cdoc?.specialties || [],
        default_hourly_rate: cdoc?.default_hourly_rate || 0,
        bonus_rate: cdoc?.bonus_rate || 0,
        default_m2_rate: cdoc?.default_m2_rate || 0,
        default_share: cdoc?.default_share || '',
        rates: cdoc?.rates || [],
        notes: cdoc?.notes || '',
        active: cdoc?.active !== false,
        is_owner: !!cdoc?.is_owner,
        quality_tier: cdoc?.quality_tier || 'Bronze',
        quality_uplift_percent: cdoc?.quality_uplift_percent || 0,
        loyalty_uplift_percent: cdoc?.loyalty_uplift_percent || 0,
        loyalty_uplift_percent_override: !!cdoc?.loyalty_uplift_percent_override,
        total_jobs_completed: cdoc?.total_jobs_completed || 0,
        contractor_type: cdoc?.contractor_type || '',
        start_date: cdoc?.start_date || '',
        gst_registered: !!cdoc?.gst_registered,
      };
      await api.put(`/contractors/${row.contractor_id}`, payload);
      updateRow(key, {
        email_orig: (row.email || '').trim(),
        mobile_orig: (row.mobile || '').trim(),
        savingBack: false,
        savedAt: new Date().toISOString(),
      });
    } catch (e) {
      updateRow(key, { savingBack: false, saveErr: e?.response?.data?.detail || e?.message || 'Save failed' });
    }
  };

  const sendAll = async () => {
    for (const it of items) {
      if (isSent(it.child_id, it.ctype)) continue;
      const key = `${it.child_id}|${it.ctype}`;
      if (!isEmailValid((rowState[key] || {}).email)) continue;
      await sendOne(it.child_id, it.ctype);
    }
  };

  useEffect(() => {
    if (onChange) onChange({ send_state: sendState, row_state: rowState });
  }, [sendState, rowState, onChange]);

  const allSent = items.length > 0 && items.every(i => isSent(i.child_id, i.ctype));
  const sentCount = items.filter(i => isSent(i.child_id, i.ctype)).length;

  return (
    <div className="space-y-4" data-testid="wf-per-child-contractor-send-bundle">
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">Send Contractor Reports</div>
            <div className="text-sm mt-1 text-amber-800">
              {params.prompt || 'Send each Contractor Report to its recipient. One bundle per (quote × contractor). Edit the email or mobile inline if the contractor record needs an update.'}
            </div>
          </div>
        </div>
        <div className="mt-3 p-2 rounded bg-white border border-amber-200 text-[11px] text-amber-800">
          Sends a real email with the contractor report attached.
        </div>
      </div>

      {items.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No contractor reports selected in the previous step. Go back and include at least one.
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
            <div className="text-xs text-slate-600">
              <b className="text-slate-800">{sentCount}</b> of <b className="text-slate-800">{items.length}</b> sent
            </div>
            <button
              type="button"
              onClick={sendAll}
              disabled={allSent || busyKeys.size > 0}
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="wf-send-bundle-send-all"
            >
              <Send size={12} /> {allSent ? 'All sent ✓' : (busyKeys.size > 0 ? 'Sending…' : 'Send all')}
            </button>
          </div>

          <div className="space-y-2">
            {items.map(it => {
              const key = `${it.child_id}|${it.ctype}`;
              const row = rowState[key] || {};
              const sent = isSent(it.child_id, it.ctype);
              const err = errFor(it.child_id, it.ctype);
              const busy = busyKeys.has(key);
              const label = `${it.quote_type_ref || ''}${it.variant_label ? ` · ${it.variant_label}` : ''} — ${it.ctype}`;
              const emailValid = isEmailValid(row.email);
              const dirty = (row.email || '') !== (row.email_orig || '') || (row.mobile || '') !== (row.mobile_orig || '');
              return (
                <div key={key} className={`p-3 border rounded-lg ${sent ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200'}`} data-testid={`wf-send-row-${it.child_id}-${it.ctype.replace(/\s+/g, '-')}`}>
                  <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-800 truncate">{label}</span>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{it.appraisal_no}</span>
                        {sent && (
                          <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                            <CheckCircle2 size={10} /> Sent
                          </span>
                        )}
                        {!row.contractor_id && (
                          <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                            <AlertTriangle size={10} /> No contractor assigned
                          </span>
                        )}
                      </div>
                      {row.contractor_name && (
                        <div className="text-[11px] text-slate-600 mt-1 flex items-center gap-1">
                          <User size={11} /> {row.contractor_name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Editable contact fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2 pl-3 border-l-2 border-slate-200">
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Email <span className="text-rose-500">*</span></label>
                      <input
                        type="email"
                        value={row.email || ''}
                        onChange={(e) => updateRow(key, { email: e.target.value })}
                        disabled={sent}
                        placeholder="contractor@email.com"
                        className={`w-full px-2 py-1 text-xs border rounded ${row.email && !emailValid ? 'border-rose-400 bg-rose-50' : 'border-slate-300'} disabled:opacity-60`}
                        data-testid={`wf-send-row-email-${it.child_id}-${it.ctype.replace(/\s+/g, '-')}`}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Mobile</label>
                      <input
                        type="tel"
                        value={row.mobile || ''}
                        onChange={(e) => updateRow(key, { mobile: e.target.value })}
                        disabled={sent}
                        placeholder="021 XXX XXXX"
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded disabled:opacity-60"
                        data-testid={`wf-send-row-mobile-${it.child_id}-${it.ctype.replace(/\s+/g, '-')}`}
                      />
                    </div>
                  </div>

                  {/* Sub-actions row: Save-to-contractor + Send */}
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[10px] text-slate-500">
                      {sent && sendState[key]?.sent_at && <span className="text-emerald-700">✓ Sent {new Date(sendState[key].sent_at).toLocaleString()} to {sendState[key].to_email}</span>}
                      {err && <span className="text-rose-700">Error: {err}</span>}
                      {row.savedAt && !dirty && <span className="text-emerald-700">✓ Contractor record updated</span>}
                      {row.saveErr && <span className="text-rose-700">Contractor save failed: {row.saveErr}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {row.contractor_id && dirty && !sent && (
                        <button
                          type="button"
                          onClick={() => saveContractorRecord(key)}
                          disabled={!!row.savingBack || !emailValid}
                          className="px-2.5 py-1.5 rounded-md text-[10.5px] font-semibold text-indigo-700 bg-white border border-indigo-300 hover:bg-indigo-50 disabled:opacity-40 inline-flex items-center gap-1"
                          data-testid={`wf-send-row-save-contractor-${it.child_id}-${it.ctype.replace(/\s+/g, '-')}`}
                          title="Persist the edited email + mobile back to the contractor's record"
                        >
                          <Save size={10} /> {row.savingBack ? 'Saving…' : 'Save to contractor'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => sendOne(it.child_id, it.ctype)}
                        disabled={sent || busy || !emailValid}
                        className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1.5 shrink-0"
                        data-testid={`wf-send-row-btn-${it.child_id}-${it.ctype.replace(/\s+/g, '-')}`}
                        title={emailValid ? 'Send this report' : 'Enter a valid email first'}
                      >
                        <Send size={12} /> {sent ? 'Sent' : (busy ? 'Sending…' : 'Send')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {globalErr && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{globalErr}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-send-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-send-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={!allSent}
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-send-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
